import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminUsersPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  created_at: string | null;
  last_login_at: string | null;
  subscription_status: string;
  subscription_period_end: string | null;
  subscription_managed_by: string;
  plan_interval: string | null;
  is_suspended: boolean;
  stripe_customer_id: string | null;
  screening_count: number;
  total_tokens: number;
  estimated_cost_usd: number;
  opensanctions_calls: number;
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Modal state
  const [grantModal, setGrantModal] = useState<AdminUser | null>(null);
  const [grantExpiry, setGrantExpiry] = useState('');
  const [deleteModal, setDeleteModal] = useState<AdminUser | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [suspendModal, setSuspendModal] = useState<{ user: AdminUser; suspend: boolean } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = searchDebounced ? { search: searchDebounced } : {};
      const res = await axios.get(`${API_BASE}/admin/users`, { params });
      setUsers(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [searchDebounced]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleGrantSubscription = async () => {
    if (!grantModal) return;
    setActionLoading(true);
    try {
      await axios.put(`${API_BASE}/admin/users/${grantModal.id}/subscription`, {
        subscription_status: 'active',
        managed_by: 'manual',
        subscription_period_end: grantExpiry || undefined,
      });
      setGrantModal(null);
      setGrantExpiry('');
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to grant subscription.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeSubscription = async (user: AdminUser) => {
    if (!window.confirm(`Revoke subscription for ${user.email}?`)) return;
    try {
      await axios.put(`${API_BASE}/admin/users/${user.id}/subscription`, {
        subscription_status: 'none',
        managed_by: 'stripe',
      });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to revoke subscription.');
    }
  };

  const handleSuspendConfirm = async () => {
    if (!suspendModal) return;
    setActionLoading(true);
    try {
      await axios.put(`${API_BASE}/admin/users/${suspendModal.user.id}/suspend`, {
        suspended: suspendModal.suspend,
      });
      setSuspendModal(null);
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update suspension.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    if (deleteConfirmEmail.toLowerCase() !== deleteModal.email.toLowerCase()) {
      alert('Email does not match.');
      return;
    }
    setActionLoading(true);
    try {
      await axios.delete(`${API_BASE}/admin/users/${deleteModal.id}`);
      setDeleteModal(null);
      setDeleteConfirmEmail('');
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete user.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  };

  const formatRelative = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(iso);
  };

  const getPlanLabel = (user: AdminUser) => {
    if (user.subscription_managed_by === 'manual') return 'Manual';
    if (!user.plan_interval) return '—';
    if (user.plan_interval === 'month') return 'Monthly';
    if (user.plan_interval === 'year') return 'Annual';
    return user.plan_interval;
  };

  const formatTokens = (count: number) => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return String(count);
  };

  const formatCost = (usd: number) => {
    if (usd === 0) return '$0.00';
    if (usd < 0.01) return '<$0.01';
    return `$${usd.toFixed(2)}`;
  };

  const getStatusBadge = (user: AdminUser) => {
    if (user.is_suspended) return <span className="badge badge-suspended">Suspended</span>;
    if (user.subscription_status === 'active') return <span className="badge badge-active">Active</span>;
    if (user.subscription_status === 'trialing') return <span className="badge badge-active">Trialing</span>;
    if (user.subscription_status === 'past_due') return <span className="badge badge-warning">Past Due</span>;
    if (user.subscription_status === 'canceled') return <span className="badge badge-canceled">Canceled</span>;
    return <span className="badge badge-none">Free</span>;
  };

  return (
    <div className="admin-users-page">
      <div className="admin-users-header">
        <h1>User Management</h1>
        <p className="admin-users-subtitle">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="admin-users-search">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-search-input"
        />
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading users...</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="admin-users-table-wrapper">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Next Billing</th>
                  <th>Last Login</th>
                  <th>Screenings</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                  <th>OS Calls</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className={user.is_suspended ? 'row-suspended' : ''}>
                    <td>{user.name || '—'}</td>
                    <td className="email-cell">{user.email}</td>
                    <td>{getStatusBadge(user)}</td>
                    <td>
                      <span className={`plan-type ${user.subscription_managed_by}`}>
                        {getPlanLabel(user)}
                      </span>
                    </td>
                    <td>{user.subscription_status === 'active' ? formatDate(user.subscription_period_end) : '—'}</td>
                    <td className="last-login-cell">{formatRelative(user.last_login_at)}</td>
                    <td>{user.screening_count}</td>
                    <td title={`${user.total_tokens.toLocaleString()} tokens`}>{formatTokens(user.total_tokens)}</td>
                    <td>{formatCost(user.estimated_cost_usd)}</td>
                    <td>{user.opensanctions_calls}</td>
                    <td className="actions-cell">
                      {user.subscription_status === 'active' && user.subscription_managed_by === 'manual' ? (
                        <button className="btn-action btn-revoke" onClick={() => handleRevokeSubscription(user)}>
                          Revoke
                        </button>
                      ) : (
                        <button className="btn-action btn-grant" onClick={() => { setGrantModal(user); setGrantExpiry(''); }}>
                          Grant
                        </button>
                      )}
                      <button
                        className={`btn-action ${user.is_suspended ? 'btn-unsuspend' : 'btn-suspend'}`}
                        onClick={() => setSuspendModal({ user, suspend: !user.is_suspended })}
                      >
                        {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                      </button>
                      <button className="btn-action btn-delete" onClick={() => { setDeleteModal(user); setDeleteConfirmEmail(''); }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={11} className="no-results">No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="admin-users-cards">
            {users.map((user) => (
              <div key={user.id} className={`admin-user-card ${user.is_suspended ? 'card-suspended' : ''}`}>
                <div className="card-header">
                  <div>
                    <div className="card-name">{user.name || '—'}</div>
                    <div className="card-email">{user.email}</div>
                  </div>
                  {getStatusBadge(user)}
                </div>
                <div className="card-details">
                  <div className="card-detail">
                    <span className="card-label">Plan:</span>
                    <span className={`plan-type ${user.subscription_managed_by}`}>{getPlanLabel(user)}</span>
                  </div>
                  <div className="card-detail">
                    <span className="card-label">Next Billing:</span> {user.subscription_status === 'active' ? formatDate(user.subscription_period_end) : '—'}
                  </div>
                  <div className="card-detail">
                    <span className="card-label">Last Login:</span> {formatRelative(user.last_login_at)}
                  </div>
                  <div className="card-detail">
                    <span className="card-label">Screenings:</span> {user.screening_count}
                  </div>
                  <div className="card-detail">
                    <span className="card-label">Tokens:</span> {formatTokens(user.total_tokens)} ({formatCost(user.estimated_cost_usd)})
                  </div>
                  <div className="card-detail">
                    <span className="card-label">OS Calls:</span> {user.opensanctions_calls}
                  </div>
                  <div className="card-detail">
                    <span className="card-label">Signed Up:</span> {formatDate(user.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  {user.subscription_status === 'active' && user.subscription_managed_by === 'manual' ? (
                    <button className="btn-action btn-revoke" onClick={() => handleRevokeSubscription(user)}>Revoke</button>
                  ) : (
                    <button className="btn-action btn-grant" onClick={() => { setGrantModal(user); setGrantExpiry(''); }}>Grant</button>
                  )}
                  <button
                    className={`btn-action ${user.is_suspended ? 'btn-unsuspend' : 'btn-suspend'}`}
                    onClick={() => setSuspendModal({ user, suspend: !user.is_suspended })}
                  >
                    {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                  </button>
                  <button className="btn-action btn-delete" onClick={() => { setDeleteModal(user); setDeleteConfirmEmail(''); }}>Delete</button>
                </div>
              </div>
            ))}
            {users.length === 0 && <div className="no-results">No users found.</div>}
          </div>
        </>
      )}

      {/* Grant Subscription Modal */}
      {grantModal && (
        <div className="modal-overlay" onClick={() => setGrantModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Grant Subscription</h2>
            <p>Grant an active subscription to <strong>{grantModal.email}</strong>.</p>
            <div className="modal-field">
              <label>Expiry Date (optional):</label>
              <input
                type="date"
                value={grantExpiry}
                onChange={(e) => setGrantExpiry(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-action btn-grant" onClick={handleGrantSubscription} disabled={actionLoading}>
                {actionLoading ? 'Granting...' : 'Grant'}
              </button>
              <button className="btn-action btn-cancel" onClick={() => setGrantModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Confirmation Modal */}
      {suspendModal && (
        <div className="modal-overlay" onClick={() => setSuspendModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{suspendModal.suspend ? 'Suspend User' : 'Unsuspend User'}</h2>
            <p>
              {suspendModal.suspend
                ? `Suspend ${suspendModal.user.email}? They will be logged out and unable to access the platform.`
                : `Unsuspend ${suspendModal.user.email}? They will be able to log in again.`}
            </p>
            <div className="modal-actions">
              <button
                className={`btn-action ${suspendModal.suspend ? 'btn-suspend' : 'btn-unsuspend'}`}
                onClick={handleSuspendConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : suspendModal.suspend ? 'Suspend' : 'Unsuspend'}
              </button>
              <button className="btn-action btn-cancel" onClick={() => setSuspendModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete User</h2>
            <p>This will permanently delete <strong>{deleteModal.email}</strong> and all their data.</p>
            <div className="modal-field">
              <label>Type the email to confirm:</label>
              <input
                type="text"
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder={deleteModal.email}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn-action btn-delete"
                onClick={handleDeleteConfirm}
                disabled={actionLoading || deleteConfirmEmail.toLowerCase() !== deleteModal.email.toLowerCase()}
              >
                {actionLoading ? 'Deleting...' : 'Delete User'}
              </button>
              <button className="btn-action btn-cancel" onClick={() => setDeleteModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
